import { createContext, useContext } from 'react';
import type { ThemeMode } from './ptcThemes';

/**
 * Theme mode resolution: OS `prefers-color-scheme` is the default; an explicit
 * user choice (the header sun/moon toggle) is persisted to localStorage under
 * `tl:theme` and overrides the OS from then on. While no explicit choice is
 * stored, the app live-follows OS theme changes.
 *
 * This module holds the context + hook + helpers (no components) so the
 * provider file can export only React components (keeps react-refresh happy).
 */
export const THEME_STORAGE_KEY = 'tl:theme';

/** The root attribute index.css keys its dark-mode variable overrides off of. */
export const THEME_ATTRIBUTE = 'data-tl-theme';

export function readStoredMode(): ThemeMode | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null; // storage unavailable (private mode / blocked) — fall back to OS
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Exported for tests: stored override wins; otherwise the OS preference. */
export function resolveInitialMode(): ThemeMode {
  return readStoredMode() ?? (systemPrefersDark() ? 'dark' : 'light');
}

export interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined,
);

/**
 * A stable no-op fallback used when `useThemeMode` is called outside a
 * `ThemeProvider`. This keeps the many existing component/page tests (which
 * render without the provider) working without wrapping every one of them —
 * they simply see a static `light` mode and inert setters, matching the app's
 * default. The real app always mounts inside `ThemeProvider`.
 */
const NOOP_THEME: ThemeContextValue = {
  mode: 'light',
  setMode: () => {},
  toggleMode: () => {},
};

export function useThemeMode(): ThemeContextValue {
  return useContext(ThemeContext) ?? NOOP_THEME;
}
